import { z } from 'zod'

/**
 * 청구 API 계약 (BIZ-15). 설계 명세서 5.6·5.7.
 *
 * 배송 완료일 기준으로 월 청구서를 만든다. 돈이 걸린 영역이라 부호·이중청구는
 * DB 제약이 최종 방어(invoice_line 부호 CHECK, ORDER 라인 주문당 1회 유니크).
 * 계약은 입력·응답 형태만 정하고, 금액 계산은 도메인이 스냅샷(order_item)에서 뽑는다.
 */

export const invoiceStatus = z.enum([
  'DRAFT', 'REVIEWED', 'CONFIRMED', 'SENT', 'AWAITING_PAYMENT',
  'PAID', 'PARTIALLY_PAID', 'OVERDUE', 'VOID',
])
export type InvoiceStatus = z.infer<typeof invoiceStatus>

export const invoiceLineType = z.enum([
  'ORDER', 'CANCELLATION_FEE', 'PRIOR_PERIOD_ADJUSTMENT', 'POINT_REDEEM', 'DISCOUNT', 'ETC',
])
export type InvoiceLineType = z.infer<typeof invoiceLineType>

/** billing_month 형식 'YYYY-MM'. */
export const billingMonth = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'YYYY-MM 형식이어야 합니다')

// ── 조회 응답 ───────────────────────────────────────────────

export const invoiceLineView = z.object({
  id: z.number().int(),
  lineType: invoiceLineType,
  orderId: z.number().int().nullable(),
  orderNo: z.string().nullable(),
  description: z.string(),
  partner: z.string().nullable(), // 받는 기업(거래처). 통계와 같은 규칙. ORDER 라인만, 조정/취소는 null.
  amount: z.number().int(),
})
export type InvoiceLineView = z.infer<typeof invoiceLineView>

/** 목록 한 줄. 금액은 기업 관리자·운영자만 본다(담당자는 접근 자체가 없다). */
export const invoiceListItem = z.object({
  id: z.number().int(),
  companyId: z.number().int(),
  companyName: z.string().nullable(),
  billingMonth: z.string(),
  status: invoiceStatus,
  subtotal: z.number().int(),
  adjustmentTotal: z.number().int(),
  totalAmount: z.number().int(),
  dueAt: z.string().nullable(),
  paidAmount: z.number().int(),
  createdAt: z.string(),
})
export type InvoiceListItem = z.infer<typeof invoiceListItem>

export const invoiceListResponse = z.object({
  items: z.array(invoiceListItem),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
})
export type InvoiceListResponse = z.infer<typeof invoiceListResponse>

export const invoiceDetail = invoiceListItem.extend({
  confirmedAt: z.string().nullable(),
  issuedAt: z.string().nullable(),
  paidAt: z.string().nullable(),
  // 공급받는 자(기업) 식별정보 — 거래명세서·청구서 PDF 발급용(BIZ-61). 목록엔 없고 상세에만.
  bizRegNo: z.string().nullable(), // 사업자등록번호
  ceoName: z.string().nullable(), // 대표자명
  companyAddress: z.string().nullable(), // 사업장 주소
  lines: z.array(invoiceLineView),
})
export type InvoiceDetail = z.infer<typeof invoiceDetail>

// ── 요청 ────────────────────────────────────────────────────

export const invoiceListQuery = z.object({
  status: invoiceStatus.optional(),
  companyId: z.coerce.number().int().positive().optional(), // 운영자만
  billingMonth: billingMonth.optional(),
  limit: z.coerce.number().int().min(1).max(100).optional().default(20),
  offset: z.coerce.number().int().min(0).optional().default(0),
})
export type InvoiceListQuery = z.infer<typeof invoiceListQuery>

/** 청구서 생성 배치. 특정 기업만 좁히려면 companyId. */
export const invoiceGenerateRequest = z.object({
  billingMonth,
  companyId: z.number().int().positive().optional(),
})
export type InvoiceGenerateRequest = z.infer<typeof invoiceGenerateRequest>

/** 상태 전이. 부분 납부·연체엔 paidAmount(0~total, int4가 아니라 int8 상한). */
export const invoiceTransitionRequest = z.object({
  to: invoiceStatus,
  paidAmount: z.number().int().min(0).max(9_007_199_254_740_991).optional(),
})
export type InvoiceTransitionRequest = z.infer<typeof invoiceTransitionRequest>

/** DRAFT 청구서에 수동 조정 라인 추가(전월 조정·할인·기타). ORDER류는 배치만 넣는다. */
export const invoiceLineAddRequest = z
  .object({
    lineType: z.enum(['PRIOR_PERIOD_ADJUSTMENT', 'DISCOUNT', 'ETC']),
    description: z.string().min(1).max(200),
    // JS 안전 정수 범위로 제한 — 정밀도 손실·int8 오버플로(500)를 입력 단계에서 막는다.
    amount: z
      .number()
      .int()
      .min(-9_007_199_254_740_991)
      .max(9_007_199_254_740_991)
      .refine((n) => n !== 0, '0은 넣을 수 없습니다'),
    orderId: z.number().int().positive().optional(),
  })
  .refine((d) => d.lineType !== 'DISCOUNT' || d.amount < 0, {
    message: '할인 라인은 음수여야 합니다',
    path: ['amount'],
  })
export type InvoiceLineAddRequest = z.infer<typeof invoiceLineAddRequest>

export const InvoiceErrorCode = {
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  INVOICE_NOT_FOUND: 'INVOICE_NOT_FOUND',
  FORBIDDEN: 'FORBIDDEN',
  INVOICE_LOCKED: 'INVOICE_LOCKED', // CONFIRMED 이상이라 수정 불가
  TRANSITION_NOT_ALLOWED: 'TRANSITION_NOT_ALLOWED',
  PAID_AMOUNT_REQUIRED: 'PAID_AMOUNT_REQUIRED', // 부분 납부엔 금액 필요
  PAID_AMOUNT_INVALID: 'PAID_AMOUNT_INVALID', // 부분 납부액이 0~총액 범위 밖(총액 이상이면 완납=PAID)
  AMOUNT_OUT_OF_RANGE: 'AMOUNT_OUT_OF_RANGE', // 라인 합이 허용 범위를 넘김
} as const
export type InvoiceErrorCode = (typeof InvoiceErrorCode)[keyof typeof InvoiceErrorCode]
